import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import type { WidgetResponse } from '@/api/widgets/widgets.types';

interface WidgetPreviewProps {
    widgetId?: string;
    widgetData?: WidgetResponse;
    updatedAt?: string;
    isCreateMode?: boolean;
}

export function WidgetPreview({ widgetData, updatedAt, isCreateMode = false }: WidgetPreviewProps) {
    const [isLoading, setIsLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(Date.now());
    const iframeRef = useRef<HTMLIFrameElement>(null);
    
    // Reset loading state and refresh key when updatedAt changes (after save)
    useEffect(() => {
        if (widgetData && updatedAt) {
            setIsLoading(true);
            setRefreshKey(Date.now()); // Force new key for preview
            const timer = setTimeout(() => setIsLoading(false), 1500);
            return () => clearTimeout(timer);
        }
    }, [updatedAt, widgetData]); // Trigger on updatedAt change
    
    // Generate the preview HTML with the actual widget script - only use saved widgetData
    const generatePreviewHTML = useCallback(() => {
        // Use the actual chatbot ID from widget data, or 'preview' in create mode
        const chatbotId = widgetData?.chatbotId || (isCreateMode ? 'preview' : 'preview');
        
        return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta http-equiv="Content-Security-Policy" content="default-src 'self' https: data: blob:; script-src 'self' 'unsafe-inline' 'unsafe-eval' https://cdn.chat.voicebot.studio https://*.voicebot.studio https://unpkg.com https://cdn.jsdelivr.net; style-src 'self' 'unsafe-inline' https:; font-src 'self' https: data:; img-src 'self' https: data: blob:; connect-src 'self' https: wss: data: blob:; frame-src 'self' https:; media-src 'self' https: blob:;">
    <title>VoiceBot Chat Widget Preview</title>
    <style>
        body {
            margin: 0;
            padding: 0;
            width: 100%;
            height: 100vh;
            position: relative;
            overflow: hidden;
            background: transparent;
        }
    </style>
</head>
<body>
    <!-- Load Voicebot Widget (Async) -->
    <script>
    (function() {
        if (window.VoicebotSDK) return; // Prevent duplicates
        
        const script = document.createElement('script');
        script.src = 'https://cdn.chat.voicebot.studio/widget.js';
        script.setAttribute('data-chatbot-id', '${chatbotId}');
        script.async = true;
        
        // Load when DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                document.head.appendChild(script);
            });
        } else {
            document.head.appendChild(script);
        }
    })();
    </script>
</body>
</html>`;
    }, [widgetData, isCreateMode]);

    // Update preview only when widget is saved (widgetData changes) or on initial load
    useEffect(() => {
        if (iframeRef.current) {
            const html = generatePreviewHTML();
            const blob = new Blob([html], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            
            iframeRef.current.src = url;
            
            // Cleanup
            return () => {
                URL.revokeObjectURL(url);
            };
        }
    }, [widgetData, refreshKey, generatePreviewHTML]);
    
    // Don't render preview until we have actual widget data (only in edit mode)
    if (!isCreateMode && !widgetData?.chatbotId) {
        return (
            <div className="relative h-full rounded-lg overflow-hidden bg-muted/30">
                <div className="absolute inset-0 z-20 bg-background/80 backdrop-blur-sm flex items-center justify-center">
                    <div className="flex items-center gap-2">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        <span className="text-sm text-muted-foreground">Loading widget data...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="relative h-full rounded-lg overflow-hidden bg-muted/30">
            {/* Loading State */}
            {isLoading && (
                <div className="absolute inset-0 z-20 bg-background/80 backdrop-blur-sm flex items-center justify-center">
                    <div className="flex items-center gap-2">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        <span className="text-sm text-muted-foreground">Loading widget...</span>
                    </div>
                </div>
            )}

            {/* Widget Preview Iframe */}
            <iframe
                ref={iframeRef}
                key={`widget-preview-${refreshKey}`}
                width="100%"
                height="100%"
                frameBorder="0"
                allow="microphone"
                className="w-full h-full rounded-lg"
                title="Widget Preview"
                sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
                onLoad={() => setIsLoading(false)}
            />
        </div>
    );
}
